import { FC, FormEvent, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { FormProvider, useTnForm } from '@thinknimble/tn-forms-react'
import { isAxiosError } from 'axios'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { Modal } from 'src/components/modal'
import { Button } from 'src/components/button'
import { Input } from 'src/components/input'
import { CustomSelect } from 'src/components/custom-select'
import { SelectWrapper } from 'src/components/select-wrapper'
import { ErrorsList } from 'src/components/errors'
import {
  AddressAutocompleteField,
  type StructuredAddress,
} from 'src/components/shared/address-autocomplete-field'
import { propertyApi } from 'src/services/properties/api'
import { propertyQueries } from 'src/services/properties/queries'
import { SUPPORTED_COUNTRIES, type CreateProperty } from 'src/services/properties/models'
import {
  AddPropertyForm,
  AddPropertyFormInputs,
  TAddPropertyForm,
} from 'src/services/properties/forms'
import type { SelectOption } from 'src/services/base-model'

interface AddPropertyModalProps {
  isOpen: boolean
  onClose: () => void
  /** Organization id the new property will be assigned to. */
  ownerId: string
}

const COUNTRY_SELECT_OPTIONS: SelectOption[] = SUPPORTED_COUNTRIES.map(({ value, label }) => ({
  label,
  value,
}))

/** Parse a numeric form field value; empty string / NaN -> undefined so the
 * server omits the field. */
const parseCount = (raw: string | undefined): number | undefined => {
  if (!raw) return undefined
  const n = Number(raw)
  return Number.isFinite(n) ? n : undefined
}

const parseDecimal = (raw: string | undefined): number | null => {
  if (!raw) return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

const AddPropertyModalInner: FC<AddPropertyModalProps> = ({ isOpen, onClose, ownerId }) => {
  const { t } = useTranslation()
  const { form, createFormFieldChangeHandler, validate, overrideForm } =
    useTnForm<TAddPropertyForm>()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  // Seed the hidden owner field every time the modal opens so it always
  // reflects the currently-selected organization.
  useEffect(() => {
    if (isOpen) {
      createFormFieldChangeHandler(form.owner)(ownerId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, ownerId])

  // Escape closes the modal.
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  // Build the current StructuredAddress from form fields for the autocomplete component.
  const addressValue = useMemo((): StructuredAddress | null => {
    const hasAnyField =
      form.street.value || form.city.value || form.stateOrRegion.value || form.postalCode.value
    if (!hasAnyField) return null
    return {
      streetAddress: form.street.value || undefined,
      city: form.city.value || undefined,
      state: form.stateOrRegion.value || undefined,
      postalCode: form.postalCode.value || undefined,
      country: form.country.value?.value || undefined,
      latitude: parseDecimal(form.latitude.value),
      longitude: parseDecimal(form.longitude.value),
      placeId: form.placeId.value || null,
      rawAddress: [
        form.street.value,
        form.city.value,
        form.stateOrRegion.value,
        form.postalCode.value,
      ]
        .filter(Boolean)
        .join(', '),
    }
  }, [
    form.street.value,
    form.city.value,
    form.stateOrRegion.value,
    form.postalCode.value,
    form.country.value,
    form.latitude.value,
    form.longitude.value,
    form.placeId.value,
  ])

  const handleAddressChange = useCallback(
    (addr: StructuredAddress | null) => {
      createFormFieldChangeHandler(form.street)(addr?.streetAddress ?? '')
      createFormFieldChangeHandler(form.city)(addr?.city ?? '')
      createFormFieldChangeHandler(form.stateOrRegion)(addr?.state ?? '')
      createFormFieldChangeHandler(form.postalCode)(addr?.postalCode ?? '')
      createFormFieldChangeHandler(form.latitude)(
        addr?.latitude != null ? String(addr.latitude) : '',
      )
      createFormFieldChangeHandler(form.longitude)(
        addr?.longitude != null ? String(addr.longitude) : '',
      )
      createFormFieldChangeHandler(form.placeId)(addr?.placeId ?? '')

      // If autocomplete returned a country, match it to a SUPPORTED_COUNTRIES option
      if (addr?.country) {
        const countryCode = addr.country.toUpperCase()
        const match = COUNTRY_SELECT_OPTIONS.find((c) => c.value === countryCode)
        if (match) {
          createFormFieldChangeHandler(form.country)(match)
        }
      } else {
        createFormFieldChangeHandler(form.country)(undefined)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createFormFieldChangeHandler],
  )

  const { mutate: createProperty, isPending } = useMutation({
    mutationFn: (input: CreateProperty) => propertyApi.create(input),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: propertyQueries.all() })
      toast.success(t('propertyForm.createdToast'))
      onClose()
      navigate(`/properties/${created.id}`)
    },
    onError: (error: unknown) => {
      if (isAxiosError(error) && error.response?.status === 400 && error.response.data) {
        const data = error.response.data as Record<string, string[] | string>
        // Surface field-level errors inline on the matching form field.
        // DRF returns snake_case keys — map the ones we care about.
        const serverToForm: Record<string, keyof AddPropertyFormInputs> = {
          name: '_name',
          country: 'country',
          street: 'street',
          city: 'city',
          state_or_region: 'stateOrRegion',
          postal_code: 'postalCode',
          bedroom_cnt: 'bedroomCnt',
          bathroom_cnt: 'bathroomCnt',
          owner: 'owner',
        }
        let matched = false
        for (const [serverKey, formKey] of Object.entries(serverToForm)) {
          const raw = data[serverKey]
          if (!raw) continue
          matched = true
          const messages = Array.isArray(raw) ? raw : [raw]
          // tn-forms doesn't expose a public "set errors" API, so we push
          // directly into the field's errors array — the ErrorsList reads it
          // back on the next render.
          ;(form[formKey] as { errors: { code: string; message: string }[] }).errors = messages.map(
            (message, idx) => ({
              code: `server-${serverKey}-${idx}`,
              message,
            }),
          )
        }
        if (matched) {
          // Force a re-render so the newly populated errors are rendered by
          // the ErrorsList components.
          overrideForm(form)
          return
        }
      }
      toast.error(t('propertyForm.createError'))
    },
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    validate()
    if (!form.isValid) return

    const countryValue = form.country.value?.value

    if (!countryValue) return // RequiredValidator should have caught this

    const input: CreateProperty = {
      name: form._name.value ?? '',
      country: countryValue,
      owner: form.owner.value ?? '',
      description: null,
      street: form.street.value || null,
      city: form.city.value || null,
      stateOrRegion: form.stateOrRegion.value || null,
      postalCode: form.postalCode.value || null,
      latitude: parseDecimal(form.latitude.value),
      longitude: parseDecimal(form.longitude.value),
      placeId: form.placeId.value || null,
      bedroomCnt: parseCount(form.bedroomCnt.value) ?? null,
      bathroomCnt: parseCount(form.bathroomCnt.value) ?? null,
    }
    createProperty(input)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('propertyForm.addTitle')} size="lg">
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="add-property-form">
        <div>
          <Input
            autoFocus
            label={form._name.label}
            placeholder={form._name.placeholder}
            value={form._name.value ?? ''}
            onChange={(e) => createFormFieldChangeHandler(form._name)(e.target.value)}
            onBlur={() => form._name.validate()}
            data-testid="add-property-name"
          />
          <ErrorsList errors={form._name.errors} />
        </div>

        <div>
          <SelectWrapper label={form.country.label} data-testid="add-property-country">
            <CustomSelect
              options={COUNTRY_SELECT_OPTIONS}
              values={form.country.value ? [form.country.value] : []}
              onChange={(values) => createFormFieldChangeHandler(form.country)(values[0])}
              placeholder={form.country.placeholder}
            />
          </SelectWrapper>
          <ErrorsList errors={form.country.errors} />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-primary">
            {t('propertyForm.address')}
          </label>
          <AddressAutocompleteField
            value={addressValue}
            onChange={handleAddressChange}
            disabled={isPending}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Input
              type="number"
              label={form.bedroomCnt.label}
              placeholder={form.bedroomCnt.placeholder}
              value={form.bedroomCnt.value ?? ''}
              onChange={(e) => createFormFieldChangeHandler(form.bedroomCnt)(e.target.value)}
            />
          </div>
          <div>
            <Input
              type="number"
              label={form.bathroomCnt.label}
              placeholder={form.bathroomCnt.placeholder}
              value={form.bathroomCnt.value ?? ''}
              onChange={(e) => createFormFieldChangeHandler(form.bathroomCnt)(e.target.value)}
            />
          </div>
        </div>

        <ErrorsList errors={form.owner.errors} />

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isPending}
            data-testid="add-property-submit"
          >
            {isPending ? t('propertyForm.creatingBtn') : t('propertyForm.createBtn')}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export const AddPropertyModal: FC<AddPropertyModalProps> = (props) => {
  // Re-mount the form when the modal toggles so fields reset between openings.
  return (
    <FormProvider<AddPropertyFormInputs>
      formClass={AddPropertyForm}
      key={props.isOpen ? 'open' : 'closed'}
    >
      <AddPropertyModalInner {...props} />
    </FormProvider>
  )
}
